import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface LinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (url: string, text?: string) => void;
  onRemove?: () => void;
  initialUrl?: string;
  initialText?: string;
  isEditing?: boolean;
  disableAutoFocus?: boolean;
}

export const LinkDialog: React.FC<LinkDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  onRemove,
  initialUrl = '',
  initialText = '',
  isEditing = false,
  disableAutoFocus = false,
}) => {
  const [url, setUrl] = useState(initialUrl);
  const [text, setText] = useState(initialText);
  const [urlError, setUrlError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setUrl(initialUrl);
      setText(initialText);
      setUrlError('');
      // Focus the URL input after a short delay (desktop only — on mobile, user taps the field)
      if (!disableAutoFocus) {
        setTimeout(() => {
          const urlInput = document.getElementById('link-url') as HTMLInputElement;
          urlInput?.focus();
        }, 100);
      }
    }
  }, [isOpen, initialUrl, initialText, disableAutoFocus]);

  // Validate URL without setting state (pure function)
  const checkUrlValidity = (urlString: string): { isValid: boolean; error: string } => {
    if (!urlString.trim()) {
      return { isValid: false, error: 'URL is required' };
    }

    // Check if it's a valid URL format
    try {
      // Allow relative URLs (starting with /)
      if (urlString.startsWith('/')) {
        return { isValid: true, error: '' };
      }

      // Allow mailto: and tel: links
      if (urlString.startsWith('mailto:') || urlString.startsWith('tel:')) {
        return { isValid: true, error: '' };
      }

      // For absolute URLs, validate with URL constructor
      const urlObj = new URL(urlString);

      // Check if protocol is http or https
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return { isValid: false, error: 'URL must start with http:// or https://' };
      }

      return { isValid: true, error: '' };
    } catch (e) {
      return { isValid: false, error: 'Please enter a valid URL (e.g., https://example.com)' };
    }
  };

  // Real-time validation
  const { isValid: isUrlValid, error: validationError } = checkUrlValidity(url);

  // Update error state only when it changes
  useEffect(() => {
    if (isOpen && validationError !== urlError) {
      setUrlError(validationError);
    }
  }, [validationError, isOpen]);

  const handleSave = () => {
    if (!isUrlValid) {
      return;
    }
    onSave(url.trim(), text.trim() || undefined);
    onClose();
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="link-dialog-title"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="link-dialog-title" className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Link' : 'Insert Link'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="link-url" className="block text-sm font-medium text-gray-700 mb-1">
              URL <span className="text-red-500">*</span>
            </label>
            <input
              id="link-url"
              type="url"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (urlError) setUrlError(''); // Clear error on change
              }}
              onKeyDown={handleKeyDown}
              placeholder="https://example.com"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent text-gray-900 ${urlError
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
                }`}
            />
            {urlError && (
              <p className="mt-1 text-sm text-red-600">{urlError}</p>
            )}
          </div>

          <div>
            <label htmlFor="link-text" className="block text-sm font-medium text-gray-700 mb-1">
              Link Text {isEditing ? '' : '(optional)'}
            </label>
            <input
              id="link-text"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isEditing ? "Current link text" : "Leave empty to use URL"}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
            />
          </div>

          <div className="flex items-center justify-between pt-4">
            <div>
              {isEditing && onRemove && (
                <button
                  onClick={handleRemove}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                >
                  Remove Link
                </button>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!isUrlValid}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-md transition-colors"
              >
                {isEditing ? 'Update' : 'Insert'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
